import type { BomComponent, BomVersion, CmdbCardRow } from '../../lib/types';
import type { ProductGroup } from './productCatalogModel';

/* --- Overview: one row per product version ------------------------------ */

export interface BomRow {
  key: string;
  productName: string;
  productId: number | null;
  version: CmdbCardRow;
  custom: boolean;
  components: BomComponent[];
  instanceCount: number;
}

const EMPTY: BomVersion = { version_id: 0, instance_count: 0, components: [] };

/** Rows in product-catalog order: official versions newest first, custom versions under their base. */
export function bomRows(groups: readonly ProductGroup[], overview: readonly BomVersion[]): BomRow[] {
  const byVersion = new Map(overview.map((entry) => [entry.version_id, entry]));
  const rows: BomRow[] = [];
  for (const group of groups) {
    // The product catalog already separated official and custom versions.
    const versions: [CmdbCardRow, boolean][] = [
      ...group.versions.flatMap((node): [CmdbCardRow, boolean][] => [
        [node.version, false], ...node.customVersions.map((v): [CmdbCardRow, boolean] => [v, true])]),
      ...group.unattachedCustomVersions.map((v): [CmdbCardRow, boolean] => [v, true]),
    ];
    for (const [version, custom] of versions) {
      const bom = byVersion.get(version.id) ?? EMPTY;
      rows.push({
        key: `version:${version.id}`,
        productName: group.name,
        productId: group.product?.id ?? null,
        version,
        custom,
        components: bom.components,
        instanceCount: bom.instance_count,
      });
    }
  }
  return rows;
}

export function kevCount(components: readonly BomComponent[]): number {
  return components.filter((component) => component.kev_affected).length;
}

function terms(search: string): string[] {
  return search.trim().toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
}

function matches(text: string, wanted: readonly string[]): boolean {
  const searchable = text.toLocaleLowerCase('de');
  return wanted.every((term) => searchable.includes(term));
}

function componentText(component: BomComponent): string {
  return [component.name, component.manufacturer, component.version, ...component.kev_cves].join(' ');
}

function rowText(row: BomRow): string {
  return [row.productName, textValue(row.version.values.VersionNr), row.version.label,
    ...row.components.map(componentText)].join(' ');
}

export function filterBomRows(rows: readonly BomRow[], search: string, kevOnly: boolean): BomRow[] {
  const wanted = terms(search);
  return rows.filter((row) => (!kevOnly || kevCount(row.components) > 0)
    && (wanted.length === 0 || matches(rowText(row), wanted)));
}

/** Rows to open for a search: those where a component (not only the version) matches. */
export function rowsWithMatchingComponents(rows: readonly BomRow[], search: string): string[] {
  const wanted = terms(search);
  if (wanted.length === 0) return [];
  return rows
    .filter((row) => row.components.some((component) => matches(componentText(component), wanted)))
    .map((row) => row.key);
}

/** One product with the rows of its versions (official newest first, custom under their base). */
export interface BomGroup {
  key: string;
  productName: string;
  productId: number | null;
  rows: BomRow[];
}

/** Group the overview rows by product, keeping their order. */
export function bomGroups(rows: readonly BomRow[]): BomGroup[] {
  const groups = new Map<string, BomGroup>();
  for (const row of rows) {
    const key = row.productId !== null ? `product:${row.productId}` : `unassigned:${row.productName}`;
    let group = groups.get(key);
    if (!group) {
      group = { key, productName: row.productName, productId: row.productId, rows: [] };
      groups.set(key, group);
    }
    group.rows.push(row);
  }
  return [...groups.values()];
}

/**
 * Groups left after search and KEV filter. A product name match keeps all of
 * its versions; otherwise only versions matching by version or component stay.
 */
export function filterBomGroups(groups: readonly BomGroup[], search: string, kevOnly: boolean): BomGroup[] {
  const wanted = terms(search);
  return groups
    .map((group) => {
      const candidates = kevOnly ? group.rows.filter((row) => kevCount(row.components) > 0) : group.rows;
      const rows = wanted.length === 0 || matches(group.productName, wanted)
        ? candidates
        : filterBomRows(candidates, search, false);
      return { ...group, rows };
    })
    .filter((group) => group.rows.length > 0);
}

/* --- Editor: collected changes ------------------------------------------ */

/** A component version picked for adding; `keepBoth` keeps other versions of its component. */
export interface PendingAdd {
  id: number;
  keepBoth: boolean;
}

export interface PendingChanges {
  add: readonly PendingAdd[];
  remove: readonly number[];
}

export const NO_CHANGES: PendingChanges = { add: [], remove: [] };

function textValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function reference(value: unknown): number | null {
  const id = Number(textValue(value));
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function catalogComponentType(row: CmdbCardRow): number | null {
  return reference(row.values.KomponenteTypeID);
}

/** Current entries of the same component in another version — replaced unless both are kept. */
export function replacedEntries(
  current: readonly BomComponent[], addition: CmdbCardRow,
): BomComponent[] {
  const type = catalogComponentType(addition);
  if (type === null) return [];
  return current.filter((component) => component.component_type_id === type
    && component.component_version_id !== addition.id);
}

/** The component version ids the Stückliste holds once the changes are saved. */
export function resultingIds(
  current: readonly BomComponent[],
  changes: PendingChanges,
  catalog: ReadonlyMap<number, CmdbCardRow>,
): Set<number> {
  const ids = new Set(current.map((component) => component.component_version_id));
  for (const id of changes.remove) ids.delete(id);
  for (const addition of changes.add) {
    const row = catalog.get(addition.id);
    if (row && !addition.keepBoth) {
      for (const replaced of replacedEntries(current, row)) ids.delete(replaced.component_version_id);
    }
    ids.add(addition.id);
  }
  return ids;
}

/** What "Speichern" sends: the difference between today and the result. */
export function saveRequest(
  current: readonly BomComponent[], result: ReadonlySet<number>,
): { add: number[]; remove: number[] } {
  const before = new Set(current.map((component) => component.component_version_id));
  return {
    add: [...result].filter((id) => !before.has(id)).sort((a, b) => a - b),
    remove: [...before].filter((id) => !result.has(id)).sort((a, b) => a - b),
  };
}

export interface ChangeSummary {
  added: number;
  replaced: number;
  removed: number;
}

export function changeSummary(
  current: readonly BomComponent[],
  changes: PendingChanges,
  catalog: ReadonlyMap<number, CmdbCardRow>,
): ChangeSummary {
  const replacedIds = new Set<number>();
  let added = 0;
  let replaced = 0;
  for (const addition of changes.add) {
    const row = catalog.get(addition.id);
    const others = row && !addition.keepBoth ? replacedEntries(current, row) : [];
    if (others.length > 0) {
      replaced += 1;
      for (const other of others) replacedIds.add(other.component_version_id);
    } else {
      added += 1;
    }
  }
  const removed = changes.remove.filter((id) => !replacedIds.has(id)).length;
  return { added, replaced, removed };
}

export function summaryText(summary: ChangeSummary): string {
  const parts = [
    summary.added ? `${summary.added} hinzugefügt` : '',
    summary.replaced ? `${summary.replaced} ersetzt` : '',
    summary.removed ? `${summary.removed} entfernt` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Keine Änderungen';
}

/** Other versions of the same product whose Stückliste equals *result*. */
export function equalVersions(
  rows: readonly BomRow[], self: BomRow, result: ReadonlySet<number>,
): BomRow[] {
  if (result.size === 0 || self.productId === null) return [];
  return rows.filter((row) => row.key !== self.key && row.productId === self.productId
    && row.components.length === result.size
    && row.components.every((component) => result.has(component.component_version_id)));
}

/* --- Editor: catalog search --------------------------------------------- */

const KIND_LABELS: Readonly<Record<string, string>> = { a: 'Software', o: 'Betriebssystem', h: 'Hardware' };

export function kindLabel(part: string): string {
  return KIND_LABELS[part.trim().toLowerCase()] ?? 'Nicht angegeben';
}

export function catalogField(row: CmdbCardRow, name: string): string {
  return textValue(row.display[name]) || textValue(row.values[name]);
}

export function catalogManufacturers(rows: readonly CmdbCardRow[]): string[] {
  const collator = new Intl.Collator('de', { sensitivity: 'base' });
  return [...new Set(rows.map((row) => catalogField(row, 'Hersteller')).filter(Boolean))].sort(collator.compare);
}

export interface CatalogFilter {
  search: string;
  /** '' for all, otherwise a CPE part (a, o, h). */
  part: string;
  manufacturer: string;
}

const SEARCH_FIELDS = ['Bezeichnung', 'Hersteller', 'Version', 'Purl', 'Vendor', 'Product', 'Update'];

/** Catalog versions matching name, manufacturer, version, PURL or CPE, plus type and manufacturer. */
export function filterCatalog(rows: readonly CmdbCardRow[], filter: CatalogFilter): CmdbCardRow[] {
  const wanted = terms(filter.search);
  const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });
  return rows
    .filter((row) => !filter.part || catalogField(row, 'Part').toLowerCase() === filter.part)
    .filter((row) => !filter.manufacturer || catalogField(row, 'Hersteller') === filter.manufacturer)
    .filter((row) => wanted.length === 0
      || matches([row.label, ...SEARCH_FIELDS.map((name) => catalogField(row, name))].join(' '), wanted))
    .sort((a, b) => collator.compare(catalogField(a, 'Bezeichnung'), catalogField(b, 'Bezeichnung'))
      || collator.compare(catalogField(b, 'Version'), catalogField(a, 'Version'))
      || a.id - b.id);
}
